import { Game } from './core/Game.js';
import { SaveManager } from './save/SaveManager.js';
import { audio } from './systems/audio/AudioSystem.js';
import { MainMenu } from './ui/MainMenu.js';
import { PlayMenu } from './ui/PlayMenu.js';
import { SettingsHubMenu } from './ui/SettingsHubMenu.js';
import { ShopMenu } from './ui/ShopMenu.js';
import { ShopEchoMenu } from './ui/ShopEchoMenu.js';
import { ShopShardsMenu } from './ui/ShopShardsMenu.js';
import { ShopSummonMenu } from './ui/ShopSummonMenu.js';
import { ShopCosmeticMenu } from './ui/ShopCosmeticMenu.js';
import { SettingsMenu } from './ui/SettingsMenu.js';
import { ControlsMenu } from './ui/ControlsMenu.js';
import { CharacterSelect } from './ui/CharacterSelect.js';
import { BossSelect } from './ui/BossSelect.js';
import { PatternPracticeMenu } from './ui/PatternPracticeMenu.js';
import { SeedRunMenu } from './ui/SeedRunMenu.js';
import { LeaderboardMenu } from './ui/LeaderboardMenu.js';
import { RepriseMenu } from './ui/RepriseMenu.js';
import { RepriseTradeMenu } from './ui/RepriseTradeMenu.js';
import { RepriseChoirMenu } from './ui/RepriseChoirMenu.js';
import { RepriseClient } from './systems/net/RepriseClient.js';
import { LoginMenu } from './ui/LoginMenu.js';
import { StatisticsMenu } from './ui/StatisticsMenu.js';
import { LoreMenu } from './ui/LoreMenu.js';
import { VersionHistoryMenu } from './ui/VersionHistoryMenu.js';
import { RedeemCodeMenu } from './ui/RedeemCodeMenu.js';
import { CloudSync } from './systems/net/CloudSync.js';
import { initMenuKeyNav } from './ui/menuKeyNav.js';
import { isMobileModeActive } from './core/deviceMode.js';
import { CHARACTERS } from './data/characters.js';
import { ActSelect } from './ui/ActSelect.js';
import { actForStartIndex } from './data/acts.js';

const canvas = document.getElementById('game-canvas');
const uiRoot = document.getElementById('ui-root');
// Lives for the whole app session — Game.js reads/writes into this same
// instance so Echoes/meta upgrades stay in sync across menu <-> run
// transitions instead of each screen loading its own stale copy.
const save = new SaveManager();
audio.setMuted(save.muted);
audio.setSfxVolume(save.sfxVolume);
audio.setMusicVolume(save.musicVolume);
const cloudSync = new CloudSync(save);

// Drives style.css's forced-landscape rotation off the same mobile-mode
// decision as TouchControls/Renderer, instead of a raw pointer:coarse media
// query — so a player who explicitly turns Mobile Controls off (champion
// select) doesn't get rotated, and one who turns it on does even on a
// device CSS wouldn't auto-detect.
function syncMobileModeAttr() {
  document.documentElement.dataset.mobileMode = isMobileModeActive(save) ? 'on' : 'off';
}
syncMobileModeAttr();
save.onChange(syncMobileModeAttr);
// WASD/Enter navigation across all menu screens — one global listener
// driven off whatever .menu-overlay is mounted, never per-screen.
initMenuKeyNav(uiRoot);

let activeScreen = null; // whatever's currently mounted in #ui-root (has a .destroy())
let currentGame = null;

// Cached Reprise world-buff snapshot (REPRISE.md §5, R2) passed into each run.
// Refreshed best-effort when the player heads toward a run; a few minutes'
// staleness on a collective, server-wide buff is harmless, and a null snapshot
// simply means no world buffs (identity), so runs are never blocked on it.
let repriseSnapshot = null;
function refreshRepriseSnapshot() {
  RepriseClient.getBuffState().then((snap) => { repriseSnapshot = snap; }).catch(() => {});
}

function clearRunOverlays() {
  document.querySelectorAll('.boss-title, .runend-overlay, .levelup-overlay, .survive-overlay, .final-warning-overlay, .tutorial-overlay, .levy-prompt').forEach((el) => el.remove());
}

function showMainMenu() {
  activeScreen?.destroy();
  // destroy(), not just stop() — this is the normal end-of-run path (every
  // run returns to the main menu before a new one can start), so this is
  // where the previous Game's renderer (text-overlay canvas + resize
  // listener, see Renderer.js) and DOM overlays actually need cleaning up.
  // Leaving this as stop()-only meant startRun()'s own currentGame?.destroy()
  // was dead code (currentGame was already null by the time it ran) and the
  // old run's HUD canvas (dash pips, transform meter, health bar) never got
  // removed — stacking on top of the next run's HUD and looking "stuck".
  currentGame?.destroy();
  clearRunOverlays();
  currentGame = null;
  activeScreen = new MainMenu(uiRoot, save, {
    onPlay: showPlay,
    onShop: showShop,
    onSettingsHub: showSettingsHub,
  });
}

function showPlay() {
  activeScreen?.destroy();
  refreshRepriseSnapshot(); // warm the world-buff snapshot ahead of a run
  // `save` is passed so PlayMenu can gate each route on whether it can mean
  // anything yet — see its MODES table.
  activeScreen = new PlayMenu(uiRoot, save, {
    // A genuinely new player on this device gets the 30-second tutorial
    // (ARCHIVE.md P4) before their first champion pick — learning to move
    // shouldn't come after a build decision. Skipping it counts as seen, and
    // either way the next Start Run goes straight through to champion select.
    onStart: () => (save.tutorialSeen ? showActSelect() : startTutorial()),
    // The Undersong Tide (BULLET-HEAVEN.md §H7). Straight to champion pick —
    // there is no act or boss to choose, which is the point: it is the one
    // route into the game that asks the player for nothing but a champion.
    onTide: () => showCharacterSelect(null, 0, { tide: true }),
    // Seeded runs (BULLET-HELL.md §D8). Straight to champion pick with the seed
    // in hand — a seeded run is Act I (see SeedRunMenu on why it doesn't route
    // through act select), so there is nothing else left to choose.
    onSeededRun: showSeedRun,
    onBossChallenge: showBossSelect,
    onCardPractice: showCardPractice,
    onLeaderboard: showLeaderboard,
    onReprise: showReprise,
    onBack: showMainMenu,
  });
}

// Act select (data/acts.js) — where a run begins. Three standalone acts, each
// rewarded and each unlocked by beating the one before it: Act I starts open,
// Act II opens on the King, Act III on the Undersong. The chosen index rides
// through champion pick to startRun, the same way BossSelect's challenge index
// does.
function showActSelect() {
  activeScreen?.destroy();
  activeScreen = new ActSelect(uiRoot, save, {
    onPick: (startBossIndex) => showCharacterSelect(null, startBossIndex),
    onBack: showPlay,
  });
}

// Seeded runs (BULLET-HELL.md §D8) — today's daily or a typed seed, carried
// through champion pick to startRun exactly as the challenge index and the
// practice card already are.
function showSeedRun() {
  activeScreen?.destroy();
  refreshRepriseSnapshot(); // a seeded run is a rewarded Act I run — it wants the world's buffs
  activeScreen = new SeedRunMenu(uiRoot, {
    onPick: (seed) => showCharacterSelect(null, 0, { seed }),
    onBack: showPlay,
  });
}

function showBossSelect() {
  activeScreen?.destroy();
  activeScreen = new BossSelect(uiRoot, save, { onPick: (bossIndex) => showCharacterSelect(bossIndex), onBack: showPlay });
}

// Card practice (BULLET-HELL.md §D8) — pick one spell card, then a champion,
// then drill it. Routes through champion select exactly as Boss Challenge does
// (a Game still needs a champion), carrying the card name alongside the boss
// index it belongs to.
function showCardPractice() {
  activeScreen?.destroy();
  activeScreen = new PatternPracticeMenu(uiRoot, save, {
    onPick: (bossIndex, practicePattern) => showCharacterSelect(bossIndex, 0, { practicePattern }),
    onBack: showPlay,
  });
}

function showSettingsHub() {
  activeScreen?.destroy();
  activeScreen = new SettingsHubMenu(uiRoot, save, {
    onSettings: showSettings,
    onControls: showControls,
    onRedeemCode: showRedeemCode,
    onVersionHistory: showVersionHistory,
    onLogin: showLogin,
    onStatistics: showStatistics,
    onLore: showLore,
    onBack: showMainMenu,
  });
}

function showShop() {
  activeScreen?.destroy();
  activeScreen = new ShopMenu(uiRoot, save, {
    onEcho: showShopEcho,
    onShards: showShopShards,
    onSummon: showShopSummon,
    onCosmetic: showShopCosmetic,
    onBack: showMainMenu,
  });
}

function showShopEcho() {
  activeScreen?.destroy();
  activeScreen = new ShopEchoMenu(uiRoot, save, { onBack: showShop });
}

function showShopShards() {
  activeScreen?.destroy();
  activeScreen = new ShopShardsMenu(uiRoot, save, { onBack: showShop });
}

function showShopSummon() {
  activeScreen?.destroy();
  activeScreen = new ShopSummonMenu(uiRoot, save, { onBack: showShop });
}

function showShopCosmetic() {
  activeScreen?.destroy();
  activeScreen = new ShopCosmeticMenu(uiRoot, save, { onBack: showShop });
}

function showLeaderboard() {
  activeScreen?.destroy();
  activeScreen = new LeaderboardMenu(uiRoot, save, { onBack: showPlay });
}

function showReprise() {
  activeScreen?.destroy();
  activeScreen = new RepriseMenu(uiRoot, save, {
    onBack: showPlay, onBarter: showRepriseTrade, onChoirs: showRepriseChoir,
  });
}

function showRepriseTrade() {
  activeScreen?.destroy();
  activeScreen = new RepriseTradeMenu(uiRoot, save, { onBack: showReprise });
}

function showRepriseChoir() {
  activeScreen?.destroy();
  activeScreen = new RepriseChoirMenu(uiRoot, save, { onBack: showReprise });
}

function showLogin() {
  activeScreen?.destroy();
  activeScreen = new LoginMenu(uiRoot, save, { onBack: showSettingsHub, onStatistics: showStatistics, cloudSync });
}

function showStatistics() {
  activeScreen?.destroy();
  activeScreen = new StatisticsMenu(uiRoot, save, { onBack: showSettingsHub });
}

function showLore() {
  activeScreen?.destroy();
  activeScreen = new LoreMenu(uiRoot, save, { onBack: showSettingsHub });
}

function showVersionHistory() {
  activeScreen?.destroy();
  activeScreen = new VersionHistoryMenu(uiRoot, { onBack: showSettingsHub });
}

function showRedeemCode() {
  activeScreen?.destroy();
  activeScreen = new RedeemCodeMenu(uiRoot, save, { onBack: showSettingsHub });
}

function showSettings() {
  activeScreen?.destroy();
  activeScreen = new SettingsMenu(uiRoot, save, { onBack: showSettingsHub });
}

function showControls() {
  activeScreen?.destroy();
  activeScreen = new ControlsMenu(uiRoot, save, { onBack: showSettingsHub });
}

// `challengeBossIndex` (0-3, or null for a normal run) comes from
// BossSelect and just rides along through champion pick to startRun — see
// Game.js's `challengeBossIndex` constructor option.
function showCharacterSelect(challengeBossIndex = null, startBossIndex = 0, { tide = false, practicePattern = null, seed = null } = {}) {
  activeScreen?.destroy();
  activeScreen = new CharacterSelect(uiRoot, save, {
    onPick: (character, opts) => startRun(character, { ...opts, challengeBossIndex, startBossIndex, tide, practicePattern, seed }),
    onBack: tide
      ? showPlay
      : (seed ? showSeedRun : (practicePattern ? showCardPractice : (challengeBossIndex != null ? showBossSelect : showActSelect))),
  });
}

// The tutorial is a Game in its own mode (no waves, no boss, no reward), so
// it goes through the same teardown as a run. `CHARACTERS[0]` is the starter
// champion — the tutorial is about the controls every champion shares, so
// which one is on screen is presentation, not a choice worth asking for.
function startTutorial() {
  activeScreen?.destroy();
  activeScreen = null;
  currentGame?.destroy();
  clearRunOverlays();
  currentGame = new Game(canvas, {
    character: CHARACTERS[0],
    save,
    onReturnToMenu: showMainMenu,
    tutorial: true,
    // Straight on to the champion pick they asked for — finishing the
    // tutorial should feel like the run starting, not like being sent back.
    onTutorialComplete: () => showCharacterSelect(),
  });
  currentGame.start();
}

function startRun(character, {
  ascension = 0, disableEchoUpgrades = false, disableShardPerks = false, disablePetBonuses = false, challengeBossIndex = null,
  startBossIndex = 0, tide = false, practicePattern = null, seed = null,
} = {}) {
  activeScreen?.destroy();
  activeScreen = null;
  currentGame?.destroy();
  clearRunOverlays();
  currentGame = new Game(canvas, {
    character, save, onReturnToMenu: showMainMenu, ascension,
    disableEchoUpgrades, disableShardPerks, disablePetBonuses, challengeBossIndex, startBossIndex, tide,
    practicePattern, seed,
    // Rewardless modes skip world buffs too — a clean, comparable fight against
    // the raw boss. That is Boss Challenge alone now: every act is a rewarded
    // route (data/acts.js) that contributes Resonance back to the world, and so
    // should also feel its buffs. `practice` is still read rather than dropped,
    // because it is the contract a rewardless route answers to.
    worldState: (challengeBossIndex != null || actForStartIndex(startBossIndex).practice)
      ? null
      : repriseSnapshot,
  });
  currentGame.start();
  if (import.meta.env.DEV) window.__game = currentGame;
}

showMainMenu();

// Best-effort session restore — never blocks the initial render (see
// LeaderboardClient/CloudSync's offline-tolerant contract). If a session
// turns out to already be logged in, re-render the main menu once its
// cloud save has been pulled down so Echoes/Shards reflect the account
// instead of whatever was last saved locally as a guest.
cloudSync.initSession().then((restored) => {
  if (restored && activeScreen instanceof MainMenu) showMainMenu();
  // Warm the world-buff snapshot once at boot too (contributions need the
  // restored session to resolve the amplified tier), so the first run of a
  // session already carries the world's buffs.
  refreshRepriseSnapshot();
});
